import { PageHeader } from '@/components/ui/PageHeader'
import { useCallback, useMemo, useState } from 'react'
import { PlatformBreadcrumbs } from '../PlatformBreadcrumbs'
import { RedisMenu } from '../RedisMenu'
import { TablePageContainer } from '@/components/ui/DataTable/PageTableContainer'
import { ComponentHeader } from '@/catalog-apps/components/component-library/shared/ComponentHeader'
import { SearchInput } from '@/catalog-apps/components/component-library/shared/SearchInput'
import { ComponentTitle } from '@/catalog-apps/components/component-library/shared/ComponentTitle'
// import { DataTable } from '@/components/ui/DataTable/DataTable'
import { useRestClient } from '@/contexts/QueryProvider'
import { IconLabsOutline } from '@icons/IconLabsOutline'
import { AppCard } from '../../Applications/AppDetailsPage/AppCard'
import { Badge } from '@/components/shadcn/badge'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { ProviderCardActionsMenu } from './ProviderCardActionsMenu'
import { IconPlusOutline } from '@icons/IconPlusOutline'
import { Button } from '@/components/shadcn/button'
import { ProviderForm } from '../Forms/ProviderForm'
// import { columns } from './PlatformUsersTable/platformUsersTableColumns'
import { motion } from 'motion/react'

const MotionAppCard = motion(AppCard)

function formatModelCostHint(model: {
  inputCostPerMillionTokens?: number | null
  outputCostPerMillionTokens?: number | null
  cachedInputCostPerMillionTokens?: number | null
}): string | null {
  const parts: string[] = []

  if (model.inputCostPerMillionTokens != null) {
    parts.push(`in $${model.inputCostPerMillionTokens}/1M`)
  }
  if (model.outputCostPerMillionTokens != null) {
    parts.push(`out $${model.outputCostPerMillionTokens}/1M`)
  }
  if (model.cachedInputCostPerMillionTokens != null) {
    parts.push(`cached in $${model.cachedInputCostPerMillionTokens}/1M`)
  }

  return parts.length > 0 ? parts.join(' · ') : null
}

export function PlatformLlmProvidersPage() {
  return (
    <>
      <PageHeader className="pr-3" title={<PlatformBreadcrumbs title="LLM Providers" />}>
        <RedisMenu />
      </PageHeader>
      <PlatformLlmProvidersPageContent />
    </>
  )
}

function PlatformLlmProvidersPageContent() {
  const { getProviderRegistrationApiListProviders } = useRestClient()
  const { data: providers } = getProviderRegistrationApiListProviders.useSuspenseQuery(undefined)
  const [searchTerm, setSearchTerm] = useState('')

  const invalidateProviders = useCallback(() => {
    getProviderRegistrationApiListProviders.invalidateQueries(undefined)
  }, [getProviderRegistrationApiListProviders])

  const filteredProviders = useMemo(() => {
    if (!providers) {
      return providers
    }

    const query = searchTerm.trim().toLowerCase()
    if (!query) {
      return providers
    }

    return providers.filter((provider) => {
      const fields = [
        provider.name,
        provider.endpoint,
        provider.description,
        ...(provider.models?.flatMap((model) => [model.model, model.name]) ?? []),
      ]

      return fields.some((value) => value?.toLowerCase().includes(query))
    })
  }, [providers, searchTerm])

  return (
    <TablePageContainer>
      <ComponentHeader className="pt-5" wrapperClassName="border-b-0">
        <ComponentTitle title="Manage LLM Providers" />
        <div className="ml-auto mr-0 flex items-center gap-2">
          <ProviderForm
            sheetTitle="Add Provider"
            sheetDescription="Add a new provider"
            onSubmit={invalidateProviders}
            sheetTrigger={
              <Button variant="ghost" size="icon">
                <IconPlusOutline />
                <span className="sr-only">Add Provider</span>
              </Button>
            }
          />
          <SearchInput value={searchTerm} onChange={setSearchTerm} />
        </div>
      </ComponentHeader>
      <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(350px,1fr))] gap-5">
        {filteredProviders?.map((p) => {
          const props = {
            name: p.name,
            description: p.description,
            icon: <IconLabsOutline size={24} />,
            cardDetails: (
              <div className="flex flex-col gap-2">
                <TooltipButton tooltip={p.endpoint ? 'Copy endpoint' : 'Not set'} side="left">
                  <Badge variant="outline" className="h-5">
                    <span className="text-xs">Endpoint:</span>
                    <span className="ml-2 line-clamp-1 text-xs font-normal">{p.endpoint ?? 'Not set'}</span>
                  </Badge>
                </TooltipButton>
                <div>
                  {p.models?.map((m) => {
                    const costHint = formatModelCostHint(m)
                    const badge = (
                      <Badge variant="secondary" className="mr-1 h-5" key={m.model}>
                        {m.name}
                      </Badge>
                    )

                    return costHint ? (
                      <TooltipButton key={m.model} tooltip={costHint} side="top">
                        {badge}
                      </TooltipButton>
                    ) : (
                      badge
                    )
                  })}
                </div>
              </div>
            ),
            cardMenu: <ProviderCardActionsMenu provider={p} />,
          }
          return <MotionAppCard layout key={p.id} {...props} />
        })}
      </div>
    </TablePageContainer>
  )
}

// {
//   "id": 1,
//   "createdAt": "2026-03-04T08:34:51.69741Z",
//   "updatedAt": "2026-03-04T08:34:51.69741Z",
//   "whodunit": null,
//   "name": "OpenAI / Azure OpenAI",
//   "endpoint": null,
//   "description": "OpenAI and Azure-hosted OpenAI models.",
//   "models": [
//     {
//       "model": "azure.gpt-4.1-2025-04-14",
//       "name": "GPT-4.1"
//     },
//     {
//       "model": "azure.gpt-4o-2024-11-20",
//       "name": "GPT-4o"
//     },
//     {
//       "model": "azure.gpt-3.5-turbo",
//       "name": "GPT-3.5 Turbo"
//     }
//   ]
// }
